import { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import useAuthStore from '../../store/authStore';
import { getPlanLimit, hasPlanFeature, isBasicPlan, isFreePlan } from '../../utils/planAccess';
import UpgradeModal from '../../components/UpgradeModal';
import { fmtDate } from '../../utils/dateFormat';
import SearchSelect from '../../components/SearchSelect';
import { confirm } from '../../components/ConfirmDialog';

const emptyForm = {
    name: '',
    role: 'waiter',
    pin: '',
    password: '',
    payment_type: 'salary',
    waiter_salary: '',
    waiter_service_pct: '',
    is_simple_kassir: false,
    username: '',
    kitchen_printer: '',
    job_title: '',
};

// Vəzifə təklifləri (combobox) — istənilən söz də yazıla bilər
const JOB_TITLE_SUGGESTIONS = ['Ofisiant', 'Barista', 'Baş ofisiant', 'Qarson', 'Menecer', 'Kassir', 'Aşpaz', 'Baş aşpaz'];

const Users = ({ ofisiantView = false } = {}) => {
    const { user: currentUser } = useAuthStore();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showUserModal, setShowUserModal] = useState(false);
    const [showPinModal, setShowPinModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);

    const [editUser, setEditUser] = useState(null);
    const [pinUser, setPinUser] = useState(null);
    const [passwordUser, setPasswordUser] = useState(null);

    const [form, setForm] = useState(emptyForm);
    const [newPin, setNewPin] = useState('');
    const [newName, setNewName] = useState(''); // PIN modalında ad da dəyişilir
    const [newJobTitle, setNewJobTitle] = useState(''); // PIN modalında vəzifə
    const [newPassword, setNewPassword] = useState('');
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [tenantServicePct, setTenantServicePct] = useState(0);
    const [printers, setPrinters] = useState([]); // mətbəx hesabı üçün printer seçimi

    // Kassir ofisiant idarəsi (superadmin icazəsi ilə)
    const [kassirCanManage, setKassirCanManage] = useState(false);
    const [showKassirAdd, setShowKassirAdd] = useState(false);
    const [kassirAddForm, setKassirAddForm] = useState({ name: '', pin: '', job_title: '' });
    const [savingKassir, setSavingKassir] = useState(false);

    useEffect(() => {
        loadUsers();
        api.get('/auth/tenant-service-percent')
            .then(r => setTenantServicePct(r.data.service_percent || 0))
            .catch(() => {});
        // Mətbəx hesabı üçün printerlər (A4/kassa xaric)
        api.get('/printers')
            .then(r => setPrinters((r.data.data || []).filter(p => p.printer_type !== 'a4' && !Number(p.is_receipt_printer || 0))))
            .catch(() => {});
    }, []);

    const adminAccount = useMemo(
        () => users.find((item) => item.role === 'admin'),
        [users]
    );

    const [search, setSearch] = useState('');
    const [userTab, setUserTab] = useState('all'); // filtr: all | waiter | other

    const staffUsers = useMemo(() => {
        const base = users.filter((item) => item.role !== 'admin');
        if (!search.trim()) return base;
        const q = search.toLowerCase().trim();
        return base.filter((u) =>
            String(u.name || '').toLowerCase().includes(q) ||
            String(u.username || '').toLowerCase().includes(q) ||
            String(u.role || '').toLowerCase().includes(q) ||
            String(u.pin_plain || '').toLowerCase().includes(q)
        );
    }, [users, search]);

    const loadUsers = async () => {
        try {
            // Ofisiant siyahısı (kassir və ya admin "Ofisiantlar" səhifəsi) → /auth/waiters (PIN daxil)
            if (currentUser?.role === 'kassir' || ofisiantView) {
                const res = await api.get('/auth/waiters');
                setUsers(res.data.data || []);
                // Admin həmişə idarə edə bilər; kassir üçün superadmin icazəsindən asılıdır
                setKassirCanManage(currentUser?.role === 'kassir' ? Number(res.data.kassir_manage_waiters || 0) === 1 : true);
            } else {
                const res = await api.get('/auth/users');
                setUsers(res.data.data || []);
            }
        } catch {
            toast.error('İstifadəçilər yüklənmədi.');
        } finally {
            setLoading(false);
        }
    };

    const closeUserModal = () => {
        setShowUserModal(false);
        setEditUser(null);
        setForm(emptyForm);
    };

    const openCreate = () => {
        setEditUser(null);
        setForm(emptyForm);
        setShowUserModal(true);
    };

    const openEdit = (user) => {
        setEditUser(user);
        setForm({
            name: user.name || '',
            role: user.role || 'waiter',
            pin: '',
            password: '',
            payment_type: user.payment_type || 'salary',
            waiter_salary: user.waiter_salary || '',
            waiter_service_pct: user.waiter_service_pct || '',
            is_simple_kassir: Boolean(Number(user.is_simple_kassir || 0)),
            job_title: user.job_title || '',
        });
        setShowUserModal(true);
    };

    const openPinModal = (user) => {
        setPinUser(user);
        setNewPin(user?.pin_plain || '');
        setNewName(user?.name || '');
        setNewJobTitle(user?.job_title || '');
        setShowPinModal(true);
    };

    const openPasswordModal = (user) => {
        setPasswordUser(user);
        setNewPassword('');
        setShowPasswordModal(true);
    };

    const roleLabel = (role) => {
        if (role === 'admin') return 'Admin';
        if (role === 'administrator') return 'Administrator';
        if (role === 'kassir') return 'Kassir';
        if (role === 'anbardar') return 'Anbardar';
        if (role === 'kitchen') return 'Mətbəx';
        return 'Ofisiant';
    };

    const roleBadge = (role) => {
        if (role === 'admin') return 'bg-primary-100 text-primary-700';
        if (role === 'administrator') return 'bg-indigo-100 text-indigo-700';
        if (role === 'kassir') return 'bg-purple-100 text-purple-700';
        if (role === 'anbardar') return 'bg-amber-100 text-amber-700';
        if (role === 'kitchen') return 'bg-cyan-100 text-cyan-700';
        return 'bg-green-100 text-green-700';
    };

    const inputClass = 'w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary-500 transition-all';
    const canUseWarehouse = hasPlanFeature(currentUser?.tenant_plan, 'warehouse');
    const freePlan = isFreePlan(currentUser?.tenant_plan);
    const basicPlan = isBasicPlan(currentUser?.tenant_plan);
    const waiterCount = staffUsers.filter((item) => item.role === 'waiter').length;
    const cashierCount = staffUsers.filter((item) => item.role === 'kassir').length;
    const maxWaiters = getPlanLimit(currentUser?.tenant_plan, 'maxWaiters');
    const maxCashiers = getPlanLimit(currentUser?.tenant_plan, 'maxCashiers');
    const availableRoles = freePlan
        ? [{ value: 'waiter', label: 'Ofisiant' }]
        : [
            { value: 'waiter', label: 'Ofisiant' },
            { value: 'administrator', label: 'Administrator' },
            ...(maxCashiers === 0 ? [] : [{ value: 'kassir', label: 'Kassir' }]),
            ...(canUseWarehouse ? [{ value: 'anbardar', label: 'Anbardar' }] : []),
            { value: 'kitchen', label: 'Mətbəx (ekran)' },
        ];

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.name.trim()) {
            toast.error('Ad daxil edin.');
            return;
        }

        if (!editUser) {
            if (form.role === 'kitchen') {
                if (form.password.trim().length < 4) { toast.error('Şifrə ən az 4 simvol olmalıdır.'); return; }
                if (!form.kitchen_printer) { toast.error('Mətbəx printeri seçin.'); return; }
            } else if (form.role === 'anbardar') {
                if (form.password.trim().length < 4) {
                    toast.error('Anbardar ucun sifre en az 4 simvol olmalidir.');
                    return;
                }
            } else if (!/^\d{4}$/.test(form.pin)) {
                toast.error('PIN tam 4 rəqəm olmalıdır.');
                return;
            }
        }

        // Administrator şifrəsi opsionaldır — verilibsə ən az 4 simvol (create + edit)
        if (form.role === 'administrator' && form.password.trim() && form.password.trim().length < 4) {
            toast.error('Şifrə ən az 4 simvol olmalıdır.');
            return;
        }

        if (form.role === 'waiter' && form.payment_type === 'service_pct') {
            const pct = Number(form.waiter_service_pct) || 0;
            if (pct <= 0) return toast.error('Servis faizi 0-dan böyük olmalıdır.');
            if (tenantServicePct > 0 && pct > tenantServicePct) {
                return toast.error(`Servis faizi ${tenantServicePct}%-dən çox ola bilməz (restoran limiti).`);
            }
        }

        const waiterPaymentPayload = form.role === 'waiter' ? {
            payment_type: form.payment_type || 'salary',
            waiter_salary: form.payment_type === 'salary' ? (Number(form.waiter_salary) || 0) : 0,
            waiter_service_pct: form.payment_type === 'service_pct' ? (Number(form.waiter_service_pct) || 0) : 0,
        } : {};
        const kassirPayload = form.role === 'kassir' ? { is_simple_kassir: form.is_simple_kassir ? 1 : 0 } : {};

        try {
            if (editUser) {
                await api.put(`/auth/users/${editUser.id}`, {
                    name: form.name.trim(),
                    role: form.role,
                    active: editUser.active,
                    job_title: (form.job_title || '').trim(),
                    ...(form.role === 'administrator' && form.password.trim() ? { password: form.password.trim() } : {}),
                    ...waiterPaymentPayload,
                    ...kassirPayload,
                });
                toast.success('İstifadəçi yeniləndi.');
            } else {
                await api.post('/auth/users', {
                    name: form.name.trim(),
                    role: form.role,
                    pin: (form.role === 'anbardar' || form.role === 'kitchen') ? '' : form.pin,
                    password: (form.role === 'anbardar' || form.role === 'kitchen')
                        ? form.password.trim()
                        : (form.role === 'administrator' ? (form.password.trim() || undefined) : undefined),
                    ...(form.role === 'kitchen' ? { kitchen_printer: form.kitchen_printer } : {}),
                    job_title: (form.job_title || '').trim(),
                    ...waiterPaymentPayload,
                    ...kassirPayload,
                });
                toast.success('İstifadəçi əlavə edildi.');
            }

            closeUserModal();
            loadUsers();
        } catch (err) {
            if (err.response?.status === 403) {
                setShowUpgradeModal(true);
            }
            toast.error(err.response?.data?.msg || 'Xeta bas verdi.');
        }
    };

    const handleToggle = async (user) => {
        try {
            await api.put(`/auth/users/${user.id}`, {
                name: user.name,
                role: user.role,
                active: user.active ? 0 : 1
            });
            toast.success(user.active ? 'Deaktiv edildi.' : 'Aktiv edildi.');
            loadUsers();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xeta bas verdi.');
        }
    };

    const handleDelete = async (user) => {
        if (!(await confirm(`"${user.name}" silinsin?`))) return;

        try {
            await api.delete(`/auth/users/${user.id}`);
            toast.success('İstifadəçi silindi.');
            loadUsers();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Siline bilmedi.');
        }
    };

    const handlePinChange = async (e) => {
        e.preventDefault();

        const nm = String(newName || '').trim();
        if (nm.length < 2) { toast.error('Ad ən az 2 simvol olmalıdır.'); return; }
        const pinDiffers = String(newPin) !== String(pinUser?.pin_plain || '');
        if (pinDiffers && !/^\d{4}$/.test(newPin)) { toast.error('PIN tam 4 rəqəm olmalıdır.'); return; }

        const jt = String(newJobTitle || '').trim();
        try {
            if (nm !== String(pinUser?.name || '') || jt !== String(pinUser?.job_title || '')) {
                await api.put(`/auth/users/${pinUser.id}/name`, { name: nm, job_title: jt });
            }
            let licWarning = '';
            if (pinDiffers) {
                const pr = await api.put(`/auth/users/${pinUser.id}/pin`, { pin: newPin });
                licWarning = pr.data?.warning || ''; // admin PIN → lisenziya yenilənmədisə
            }
            toast.success('Yadda saxlanıldı.');
            if (licWarning) toast.error(licWarning, { duration: 9000 });
            setShowPinModal(false);
            setPinUser(null);
            setNewPin('');
            setNewName('');
            setNewJobTitle('');
            loadUsers();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xeta bas verdi.');
        }
    };

    const handlePinDelete = async () => {
        if (!pinUser) return;
        if (!(await confirm(`${pinUser.name} üçün PIN silinsin? İstifadəçi adı qalır, sadəcə PIN ilə girə bilməyəcək.`))) return;
        try {
            await api.delete(`/auth/users/${pinUser.id}/pin`);
            toast.success('PIN silindi.');
            setShowPinModal(false);
            setPinUser(null);
            setNewPin('');
            loadUsers();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinmədi.');
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();

        if (newPassword.trim().length < 4) {
            toast.error('Şifrə ən az 4 simvol olmalıdır.');
            return;
        }

        try {
            const res = await api.put(`/auth/users/${passwordUser.id}/password`, {
                password: newPassword.trim()
            });

            const loginUsername = res.data?.login_username || res.data?.username;
            if (loginUsername) {
                toast.success(`Şifrə yeniləndi. Username: ${loginUsername}`);
            } else {
                toast.success('Şifrə yeniləndi.');
            }
            if (res.data?.warning) toast.error(res.data.warning, { duration: 9000 }); // lisenziya yenilənmədi

            setShowPasswordModal(false);
            setPasswordUser(null);
            setNewPassword('');
            loadUsers();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xeta bas verdi.');
        }
    };

    const saveKassirWaiter = async (e) => {
        e.preventDefault();
        const nm = kassirAddForm.name.trim();
        const pn = String(kassirAddForm.pin || '');
        if (!nm || pn.length !== 4) return toast.error('Ad və 4 rəqəmli PIN lazımdır.');
        setSavingKassir(true);
        try {
            await api.post('/auth/users', { name: nm, role: 'waiter', pin: pn, job_title: (kassirAddForm.job_title || '').trim() });
            toast.success('Ofisiant əlavə edildi.');
            setShowKassirAdd(false);
            setKassirAddForm({ name: '', pin: '', job_title: '' });
            loadUsers();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Əlavə edilmədi.');
        } finally {
            setSavingKassir(false);
        }
    };

    const deleteKassirWaiter = async (w) => {
        if (Number(w.waiter_salary || 0) !== 0) {
            return toast.error('Yalnız maaşı 0 olan ofisianti silmək olar.');
        }
        if (!(await confirm(`${w.name} ofisianti silinsin?`))) return;
        try {
            await api.delete(`/auth/users/${w.id}`);
            toast.success('Ofisiant silindi.');
            loadUsers();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinmədi.');
        }
    };

    // ── OFISIANT VIEW (kassir + admin "Ofisiantlar"): sadə PIN idarəsi ────────
    if (currentUser?.role === 'kassir' || ofisiantView) {
        const filteredKassirUsers = !search.trim()
            ? users
            : users.filter(u =>
                String(u.name || '').toLowerCase().includes(search.toLowerCase()) ||
                String(u.pin_plain || '').toLowerCase().includes(search.toLowerCase())
            );
        return (
            <Layout>
                <div className="max-w-lg mx-auto p-4">
                    <div className="flex items-center justify-between mb-4">
                        <h1 className="text-xl font-bold text-slate-800">🔑 Ofisiantlar</h1>
                        {kassirCanManage && (
                            <button
                                onClick={() => { setKassirAddForm({ name: '', pin: '' }); setShowKassirAdd(true); }}
                                className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-bold transition-all"
                            >
                                + Ofisiant əlavə et
                            </button>
                        )}
                    </div>

                    <div className="relative mb-3">
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="🔍 Ofisiant adı və ya PIN üzrə axtar..."
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 pr-9 text-sm focus:outline-none focus:border-blue-400"
                        />
                        {search && (
                            <button
                                type="button"
                                onClick={() => setSearch('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 text-xs"
                            >
                                ✕
                            </button>
                        )}
                    </div>

                    {loading ? (
                        <p className="text-slate-400 text-center py-10">Yüklənir...</p>
                    ) : (
                        <div className="space-y-2">
                            {filteredKassirUsers.length === 0 && (
                                <p className="text-slate-400 text-center py-10">Ofisiant tapılmadı.</p>
                            )}
                            {filteredKassirUsers.map((w) => (
                                <div key={w.id} className={`flex items-center justify-between bg-white border rounded-2xl px-4 py-3 shadow-sm ${w.active ? 'border-slate-200' : 'border-slate-100 opacity-60'}`}>
                                    <div className="flex items-center gap-3">
                                        <div className={`flex h-10 w-10 items-center justify-center rounded-full font-bold text-base ${w.active ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'}`}>
                                            {w.name?.[0] || '?'}
                                        </div>
                                        <div>
                                            <p className="font-semibold text-slate-800 text-sm">
                                                {w.name}
                                                {!w.active && <span className="ml-2 text-xs font-normal text-slate-400">(passiv)</span>}
                                            </p>
                                            <p className="text-xs text-slate-400 mt-0.5">
                                                PIN:{' '}
                                                {w.pin_plain
                                                    ? <span className="font-mono font-bold text-slate-700 tracking-widest">{w.pin_plain}</span>
                                                    : w.has_pin
                                                        ? <span className="font-mono font-bold text-slate-400 tracking-widest">••••</span>
                                                        : <span className="text-slate-300 italic">təyin edilməyib</span>
                                                }
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                        <button
                                            onClick={() => openPinModal(w)}
                                            className="text-xs font-bold bg-slate-100 hover:bg-blue-100 hover:text-blue-700 text-slate-600 px-3 py-1.5 rounded-xl transition-all"
                                        >
                                            ✏️ PIN
                                        </button>
                                        {kassirCanManage && Number(w.waiter_salary || 0) === 0 && (
                                            <button
                                                onClick={() => deleteKassirWaiter(w)}
                                                title="Sil"
                                                className="text-xs font-bold bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 px-2.5 py-1.5 rounded-xl transition-all"
                                            >
                                                🗑
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* PIN change modal */}
                {showPinModal && (
                    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl shadow-xl w-full max-w-xs p-5">
                            <h2 className="text-base font-bold text-slate-800 mb-1">✏️ Ofisiant redaktə</h2>
                            <p className="text-xs text-slate-400 mb-4">Ad və PIN-i dəyişə bilərsiniz</p>
                            <form onSubmit={handlePinChange} className="space-y-3">
                                <div>
                                    <label className="text-[11px] font-semibold text-slate-500">Ad</label>
                                    <input
                                        type="text"
                                        value={newName}
                                        onChange={e => setNewName(e.target.value)}
                                        placeholder="Ofisiant adı"
                                        className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                                        autoFocus
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] font-semibold text-slate-500">Vəzifə (çekdə görünür)</label>
                                    <input
                                        type="text"
                                        list="job-title-list"
                                        value={newJobTitle}
                                        onChange={e => setNewJobTitle(e.target.value)}
                                        placeholder="Məs: Barista"
                                        className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                                    />
                                    <datalist id="job-title-list">
                                        {JOB_TITLE_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
                                    </datalist>
                                    <p className="mt-1 text-[10px] text-slate-400">Çekdə: <b>{(newJobTitle || '').trim() ? `${newJobTitle.trim()} ${newName || 'Ad'}` : (newName || 'Ad')}</b></p>
                                </div>
                                <div>
                                    <label className="text-[11px] font-semibold text-slate-500">PIN (mövcud — dəyişmək istəsən redaktə et)</label>
                                    <input
                                        type="tel"
                                        inputMode="numeric"
                                        pattern="\d{4}"
                                        maxLength={4}
                                        value={newPin}
                                        onChange={e => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                        placeholder="4 rəqəmli PIN"
                                        className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-center text-2xl font-mono tracking-widest placeholder:text-sm placeholder:tracking-normal focus:outline-none focus:border-blue-400"
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => { setShowPinModal(false); setNewPin(''); setNewName(''); }}
                                        className="flex-1 border border-slate-200 rounded-xl py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all"
                                    >
                                        Ləğv et
                                    </button>
                                    <button
                                        type="submit"
                                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-2 text-sm font-bold transition-all"
                                    >
                                        Saxla
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Kassir — yeni ofisiant əlavə modalı */}
                {showKassirAdd && (
                    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl shadow-xl w-full max-w-xs p-5">
                            <h2 className="text-base font-bold text-slate-800 mb-4">➕ Yeni ofisiant</h2>
                            <form onSubmit={saveKassirWaiter} className="space-y-3">
                                <input
                                    type="text"
                                    value={kassirAddForm.name}
                                    onChange={e => setKassirAddForm(f => ({ ...f, name: e.target.value }))}
                                    placeholder="Ad Soyad"
                                    className="w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400"
                                    autoFocus
                                />
                                <input
                                    type="text"
                                    list="job-title-list"
                                    value={kassirAddForm.job_title}
                                    onChange={e => setKassirAddForm(f => ({ ...f, job_title: e.target.value }))}
                                    placeholder="Vəzifə (məs: Barista) — çekdə görünür"
                                    className="w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400"
                                />
                                <datalist id="job-title-list">
                                    {JOB_TITLE_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
                                </datalist>
                                <input
                                    type="tel"
                                    inputMode="numeric"
                                    maxLength={4}
                                    value={kassirAddForm.pin}
                                    onChange={e => setKassirAddForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                                    placeholder="4 rəqəmli PIN"
                                    className="w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-center text-2xl font-mono tracking-widest focus:outline-none focus:border-blue-400"
                                />
                                <p className="text-[11px] text-slate-400">Ödəniş (faiz/maaş) avtomatik təyin olunur.</p>
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => setShowKassirAdd(false)}
                                        className="flex-1 border border-slate-200 rounded-xl py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all">
                                        Ləğv et
                                    </button>
                                    <button type="submit" disabled={savingKassir}
                                        className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl py-2 text-sm font-bold transition-all">
                                        {savingKassir ? '...' : 'Əlavə et'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </Layout>
        );
    }

    return (
        <Layout>
            <UpgradeModal
                open={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                title="İstifadəçi limiti dolub"
                message="Daha çox istifadəçi üçün planınızı yüksəldin."
                plans={['basic', 'pro']}
                currentPlan={currentUser?.tenant_plan}
            />
            <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'serif' }}>
                        İstifadəçilər
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">
                        {users.length} istifadeci
                    </p>
                </div>

                <button
                    onClick={openCreate}
                    className="px-4 py-2 rounded-xl text-sm font-bold text-white transition-all hover:-translate-y-0.5"
                    style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                >
                    + Yeni istifadeci
                </button>
            </div>

            {adminAccount && (
                <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-500 mb-2">
                                Admin hesab ayarları
                            </div>
                            <h2 className="text-xl font-bold text-gray-800 mb-1">
                                {adminAccount.name}
                            </h2>
                            <div className="text-sm text-gray-500">
                                Login username: <span className="font-semibold text-gray-700">{adminAccount.username}</span>
                            </div>
                            <div className="text-xs text-gray-400 mt-2">
                                Admin öz PIN və şifrəsini bu bölmədən dəyişə bilər.
                            </div>
                        </div>

                        <div className="flex gap-2 flex-wrap">
                            <button
                                onClick={() => openPinModal(adminAccount)}
                                className="px-3 py-2 rounded-xl text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all"
                            >
                                PIN dəyiş
                            </button>
                            <button
                                onClick={() => openPasswordModal(adminAccount)}
                                className="px-3 py-2 rounded-xl text-sm font-semibold bg-primary-50 text-primary-700 hover:bg-primary-100 transition-all"
                            >
                                Şifrə dəyiş
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="flex items-center justify-center h-48 text-gray-400">Yüklənir...</div>
            ) : (
              <>
                <div className="relative mb-3">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="🔍 İşçi adı, username, rol və ya PIN üzrə axtar..."
                        className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 pr-9 text-sm focus:outline-none focus:border-primary-400"
                    />
                    {search && (
                        <button
                            type="button"
                            onClick={() => setSearch('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 text-xs"
                        >
                            ✕
                        </button>
                    )}
                </div>
                {/* Filtr: Hamısı / Ofisiant / Digərləri */}
                <div className="mb-3 inline-flex rounded-xl border border-gray-200 bg-white p-1">
                    {[
                        { k: 'all',    lbl: 'Hamısı',       cnt: staffUsers.length },
                        { k: 'waiter', lbl: '🧑‍🍳 Ofisiant', cnt: staffUsers.filter(u => u.role === 'waiter').length },
                        { k: 'other',  lbl: '👤 Digərləri',  cnt: staffUsers.filter(u => u.role !== 'waiter').length },
                    ].map(({ k, lbl, cnt }) => (
                        <button key={k} type="button" onClick={() => setUserTab(k)}
                            className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${
                                userTab === k ? 'bg-primary-500 text-white' : 'text-gray-500 hover:bg-gray-50'
                            }`}>
                            {lbl} <span className={`text-[11px] ${userTab === k ? 'text-white/80' : 'text-gray-400'}`}>({cnt})</span>
                        </button>
                    ))}
                </div>
                <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase">#</th>
                                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Ad</th>
                                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Username</th>
                                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase">PIN / Şifrə</th>
                                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Rol</th>
                                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Status</th>
                                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Qeydiyyat</th>
                                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Əməliyyat</th>
                            </tr>
                        </thead>
                        <tbody>
                            {staffUsers.filter(u => userTab === 'all' || (userTab === 'waiter' ? u.role === 'waiter' : u.role !== 'waiter')).map((user) => (
                                <tr key={user.id} className="border-t border-gray-50 hover:bg-gray-50 transition-colors">
                                    <td className="px-5 py-4 text-xs font-mono text-gray-400">{user.id}</td>
                                    <td className="px-5 py-4">
                                        <div className="font-semibold text-sm text-gray-800">{user.name}</div>
                                        {user.role === 'kitchen' && user.kitchen_printer && (
                                            <div className="text-xs text-cyan-600 mt-0.5">🖨️ {user.kitchen_printer}</div>
                                        )}
                                        {user.role === 'waiter' && (user.waiter_salary > 0 || user.waiter_service_pct > 0) && (
                                            <div className="text-xs text-gray-400 mt-0.5">
                                                {user.payment_type === 'service_pct'
                                                    ? `📊 ${user.waiter_service_pct}% servis`
                                                    : `💰 ${Math.floor(user.waiter_salary)} ₼/ay`}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-5 py-4 text-sm text-gray-600">
                                        {user.username || '-'}
                                    </td>
                                    <td className="px-5 py-4">
                                        {user.pin_plain
                                            ? <span className="font-mono font-bold text-slate-700 tracking-widest text-sm">{user.pin_plain}</span>
                                            : user.has_pin
                                                ? <span className="font-mono text-slate-400 tracking-widest text-sm">••••</span>
                                                : <span className="text-xs text-slate-300 italic">yoxdur</span>}
                                    </td>
                                    <td className="px-5 py-4">
                                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${roleBadge(user.role)}`}>
                                            {roleLabel(user.role)}
                                        </span>
                                    </td>
                                    <td className="px-5 py-4">
                                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                                            user.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-500'
                                        }`}>
                                            {user.active ? 'Aktiv' : 'Passiv'}
                                        </span>
                                    </td>
                                    <td className="px-5 py-4 text-xs text-gray-400">
                                        {fmtDate(user.created_at)}
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="flex gap-1.5 flex-wrap">
                                            <button
                                                onClick={() => openEdit(user)}
                                                className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all"
                                            >
                                                Redaktə et
                                            </button>
                                            {user.role !== 'anbardar' && (
                                                <button
                                                    onClick={() => openPinModal(user)}
                                                    className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all"
                                                >
                                                    PIN dəyiş
                                                </button>
                                            )}
                                            {(user.role === 'anbardar' || user.role === 'kassir') && (
                                                <button
                                                    onClick={() => openPasswordModal(user)}
                                                    className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-primary-50 text-primary-700 hover:bg-primary-100 transition-all"
                                                >
                                                    Şifrə dəyiş
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleToggle(user)}
                                                className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-50 text-amber-600 hover:bg-amber-100 transition-all"
                                            >
                                                {user.active ? 'Deaktiv' : 'Aktiv'}
                                            </button>
                                            <button
                                                onClick={() => handleDelete(user)}
                                                className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-red-50 text-red-500 hover:bg-red-100 transition-all"
                                            >
                                                Sil
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
              </>
            )}

            {showUserModal && (
                <div
                    className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                    onClick={(e) => e.target === e.currentTarget && closeUserModal()}
                >
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
                        <h3 className="font-bold text-lg mb-5 text-gray-800">
                            {editUser ? 'İstifadəçini redaktə et' : 'Yeni istifadəçi'}
                        </h3>

                        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                    Ad Soyad
                                </label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                                    placeholder="Mes: Aysel Memmedova"
                                    className={inputClass}
                                    required
                                    autoFocus
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                    Rol
                                </label>
                                <SearchSelect
                                    value={form.role}
                                    onChange={v => setForm(prev => ({ ...prev, role: v, payment_type: 'salary', waiter_salary: '', waiter_service_pct: '' }))}
                                    options={availableRoles.map(role => ({
                                        value: role.value,
                                        label: role.label,
                                        disabled: !editUser && (
                                            (role.value === 'waiter'  && maxWaiters  !== null && waiterCount  >= maxWaiters)  ||
                                            (role.value === 'kassir'  && maxCashiers !== null && cashierCount >= maxCashiers)
                                        ),
                                    }))}
                                    placeholder="Seç..."
                                />
                            </div>

                            {/* Vəzifə — çekdə ad qarşısında görünür (məs. "Barista Rəşad") */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                    Vəzifə <span className="text-gray-300 normal-case">(çekdə görünür)</span>
                                </label>
                                <input
                                    type="text"
                                    list="job-title-list"
                                    value={form.job_title}
                                    onChange={(e) => setForm((prev) => ({ ...prev, job_title: e.target.value }))}
                                    placeholder="Seç və ya yaz — məs: Barista"
                                    className={inputClass}
                                />
                                <datalist id="job-title-list">
                                    {JOB_TITLE_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
                                </datalist>
                                <p className="mt-1 text-[11px] text-gray-400">
                                    Çekdə: <b>{(form.job_title || '').trim() ? `${form.job_title.trim()} ${form.name || 'Ad'}` : (form.name || 'Ofisiant adı')}</b>
                                </p>
                            </div>

                            {/* Waiter payment type — only shown when role=waiter */}
                            {form.role === 'waiter' && (
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                        Ödəniş növü
                                    </label>
                                    <div className="flex rounded-xl overflow-hidden border border-gray-200 mb-3">
                                        <button
                                            type="button"
                                            onClick={() => setForm(p => ({ ...p, payment_type: 'salary' }))}
                                            className={`flex-1 py-2 text-sm font-semibold transition-all ${form.payment_type === 'salary' ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
                                        >
                                            💰 Sabit maaş
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setForm(p => ({ ...p, payment_type: 'service_pct' }))}
                                            className={`flex-1 py-2 text-sm font-semibold transition-all ${form.payment_type === 'service_pct' ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
                                        >
                                            📊 Servis faizi %
                                        </button>
                                    </div>
                                    {form.payment_type === 'salary' ? (
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={form.waiter_salary}
                                            onChange={e => setForm(p => ({ ...p, waiter_salary: e.target.value }))}
                                            placeholder="Aylıq maaş (₼)"
                                            className={inputClass}
                                        />
                                    ) : (
                                        <>
                                            <input
                                                type="number"
                                                min="0"
                                                max={tenantServicePct || 100}
                                                step="0.5"
                                                value={form.waiter_service_pct}
                                                onChange={e => setForm(p => ({ ...p, waiter_service_pct: e.target.value }))}
                                                placeholder={`Faiz (%) — maks. ${tenantServicePct || '?'}%`}
                                                className={inputClass}
                                            />
                                            {tenantServicePct > 0 && (
                                                <p className="text-xs text-slate-500 mt-1">
                                                    Restoran servis haqq: <b>{tenantServicePct}%</b>. Ofisiant payı bundan çox ola bilməz.
                                                </p>
                                            )}
                                            {form.waiter_service_pct && Number(form.waiter_service_pct) > 0 && (
                                                <div className="mt-2 rounded-xl bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-700">
                                                    Nümunə: 100 ₼ hesab → ofisiant <b>{Math.floor(100 * Number(form.waiter_service_pct) / 100)} ₼</b> qazanır
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Kassir — sadə (waiter sistemi) tick */}
                            {form.role === 'kassir' && (
                                <div className="rounded-xl border border-purple-100 bg-purple-50/40 p-3">
                                    <label className="flex items-start gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={Boolean(form.is_simple_kassir)}
                                            onChange={(e) => setForm(p => ({ ...p, is_simple_kassir: e.target.checked }))}
                                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-purple-600"
                                        />
                                        <div className="flex-1">
                                            <div className="text-sm font-bold text-purple-800">📝 Sadə kassir (waiter sistemi)</div>
                                            <div className="text-[11px] text-purple-700/80 mt-0.5 leading-snug">
                                                PIN ilə daxil olduqda yalnız ofisiant səlahiyyəti — bütün masaları görür amma silə, mətbəxə yenidən göndərə və hesab bağlaya bilmir.
                                                Admin paneldən şifrə ilə daxil olduqda tam kassir səlahiyyəti qalır.
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            )}

                            {(freePlan || basicPlan) && !editUser && (
                                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                                    {freePlan
                                        ? 'Pulsuz paketdə yalnız 1 ofisiant dəstəklənir. Kassir və anbardar mövcud deyil.'
                                        : `Sadə paketdə maksimum ${maxWaiters || 2} ofisiant və ${maxCashiers || 1} kassir dəstəklənir.`}
                                </div>
                            )}

                            {!canUseWarehouse && (
                                <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                                    Bu paketdə anbardar və anbar funksiyası mövcud deyil.
                                </div>
                            )}

                            {!editUser && (
                                <>
                                    {form.role !== 'anbardar' && form.role !== 'kitchen' && (
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                                PIN kod (4 rəqəm)
                                            </label>
                                            <input
                                                type="text"
                                                value={form.pin}
                                                onChange={(e) => setForm((prev) => ({
                                                    ...prev,
                                                    pin: e.target.value.replace(/\D/g, '').slice(0, 4)
                                                }))}
                                                placeholder="0000"
                                                maxLength={4}
                                                className={`${inputClass} text-center text-2xl tracking-widest font-mono`}
                                                required
                                            />
                                        </div>
                                    )}

                                    {form.role === 'anbardar' && (
                                        <>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                                    Şifrə
                                                </label>
                                                <input
                                                    type="text"
                                                    value={form.password}
                                                    onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                                                    placeholder="En azi 4 simvol"
                                                    className={inputClass}
                                                    required
                                                />
                                            </div>

                                            <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
                                                Username avtomatik qurulacaq:
                                                <span className="font-bold ml-1">anbar_restoranadi</span>
                                            </div>
                                        </>
                                    )}

                                    {form.role === 'kitchen' && (
                                        <>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Mətbəx (printer)</label>
                                                <select value={form.kitchen_printer}
                                                    onChange={(e) => setForm((prev) => ({ ...prev, kitchen_printer: e.target.value }))}
                                                    className={inputClass} required>
                                                    <option value="">— Printer seçin —</option>
                                                    {printers.map((p) => (
                                                        <option key={p.id} value={p.name}>{p.label || p.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Şifrə</label>
                                                <input type="text" value={form.password}
                                                    onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                                                    placeholder="Ən azı 4 simvol" className={inputClass} required />
                                            </div>
                                            <div className="rounded-xl bg-cyan-50 border border-cyan-200 px-4 py-3 text-sm text-cyan-700">
                                                İstifadəçi adı avtomatik qurulacaq: <span className="font-bold">mətbəxadı_restoranadı</span> (məs. <span className="font-bold">soyuqmetbex_demo</span>) — login ekranındakı siyahıda görünəcək.
                                            </div>
                                        </>
                                    )}
                                </>
                            )}

                            {/* Administrator — opsional şifrə (create + edit). PIN yuxarıda; şifrə ilə də girə bilər. */}
                            {form.role === 'administrator' && (
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                        Şifrə <span className="normal-case text-gray-300">(istəyə bağlı)</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={form.password}
                                        onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                                        placeholder={editUser ? 'Dəyişmək üçün yeni şifrə (boş = dəyişmir)' : 'Boş buraxıla bilər — ən az 4 simvol'}
                                        className={inputClass}
                                    />
                                    <p className="mt-1 text-[11px] text-gray-400">
                                        Administrator giriş ekranında PIN və ya şifrə ilə daxil olub waiter (masalar) sisteminə düşür.
                                    </p>
                                </div>
                            )}

                            {editUser?.role === 'anbardar' && (
                                <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
                                    Anbardar username-i avtomatik saxlanır. Şifrəni dəyişmək üçün ayrıca
                                    <span className="font-semibold"> Şifrə dəyiş </span>
                                    düyməsindən istifadə edin.
                                </div>
                            )}

                            {editUser?.role === 'kassir' && (
                                <div className="rounded-xl bg-purple-50 border border-purple-200 px-4 py-3 text-sm text-purple-700">
                                    <b>PIN</b> ilə giriş → yalnız masa/sifariş görünüşü.<br/>
                                    <b>Şifrə</b> ilə giriş → tam kassir paneli.<br/>
                                    Şifrə təyin etmək üçün <span className="font-semibold">Şifrə dəyiş</span> düyməsindən istifadə edin.
                                </div>
                            )}

                            <div className="flex gap-3 mt-2">
                                <button
                                    type="button"
                                    onClick={closeUserModal}
                                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                                >
                                    Legv et
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white"
                                    style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                                >
                                    {editUser ? 'Yadda saxla' : 'Elave et'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showPinModal && (
                <div
                    className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                    onClick={(e) => e.target === e.currentTarget && setShowPinModal(false)}
                >
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h3 className="font-bold text-lg mb-2 text-gray-800">PIN dəyiş</h3>
                        <p className="text-gray-500 text-sm mb-5">{pinUser?.name}</p>

                        <form onSubmit={handlePinChange} className="flex flex-col gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                    Yeni PIN (4 reqem)
                                </label>
                                <input
                                    type="text"
                                    value={newPin}
                                    onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                    placeholder="0000"
                                    maxLength={4}
                                    className={`${inputClass} text-center text-2xl tracking-widest font-mono`}
                                    required
                                    autoFocus
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowPinModal(false)}
                                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                                >
                                    Legv et
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white"
                                    style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                                >
                                    Yadda saxla
                                </button>
                            </div>
                        </form>

                        {pinUser?.has_pin && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={handlePinDelete}
                                    className="w-full py-2.5 rounded-xl border-2 border-red-200 text-sm font-bold text-red-500 hover:bg-red-50 transition-all"
                                >
                                    🗑️ PIN-i sil (giriş bağlanır)
                                </button>
                                <p className="text-[10px] text-gray-400 mt-1.5 text-center leading-snug">
                                    İstifadəçi adı qalır, sadəcə PIN ilə girə bilməyəcək. Sonradan yeni PIN təyin edə bilərsiniz.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showPasswordModal && (
                <div
                    className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                    onClick={(e) => e.target === e.currentTarget && setShowPasswordModal(false)}
                >
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h3 className="font-bold text-lg mb-2 text-gray-800">Şifrə dəyiş</h3>
                        <p className="text-gray-500 text-sm mb-2">{passwordUser?.name}</p>
                        {passwordUser?.username && (
                            <p className="text-xs text-primary-600 mb-1">
                                Giriş adı: <span className="font-bold font-mono">{passwordUser.username}</span>
                            </p>
                        )}
                        {passwordUser?.role === 'kassir' && (
                            <p className="text-xs text-purple-500 mb-4">
                                Bu şifrə ilə tam kassir panelinə daxil olunur.
                            </p>
                        )}

                        <form onSubmit={handlePasswordChange} className="flex flex-col gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                    Yeni sifre
                                </label>
                                <input
                                    type="text"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="En azi 4 simvol"
                                    className={inputClass}
                                    required
                                    autoFocus
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowPasswordModal(false)}
                                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                                >
                                    Legv et
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white"
                                    style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                                >
                                    Yadda saxla
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default Users;
